import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, iif, lastValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppConfig, AppConfigResponse } from '../type/app-config.type';
import { APP_CONFIG_INITIALIZER } from '../constant/app-config.constant';
import { merge } from 'lodash-es';
import { ReCaptchaConfigService } from '@core/recaptcha/data-access';

@Injectable()
export class AppConfigService {
  private readonly configLoaded$ = new BehaviorSubject<boolean>(false);
  private readonly captchaConfigService = inject(ReCaptchaConfigService);
  private readonly configInitializer = inject(APP_CONFIG_INITIALIZER);
  private readonly http = inject(HttpClient);
  private config: AppConfig | null = null;

  async loadConfig(): Promise<void> {
    return lastValueFrom(
      iif(
        () => typeof this.configInitializer.configUrl === 'string',
        this.http.get<AppConfigResponse>(this.configInitializer.configUrl!),
        of(this.configInitializer.initial)
      )
    )
      .then(response => {
        this.config = merge({}, this.configInitializer.initial, response);
        this.captchaConfigService.setSiteKey(this.config.recaptchaSitekey);
        this.configLoaded$.next(true);
      })
      .catch(error => {
        this.configLoaded$.error(error);
        throw error;
      });
  }

  waitForConfig(): Observable<AppConfig> {
    return this.configLoaded$.asObservable().pipe(
      filter(loaded => loaded === true),
      map(() => this.getConfig())
    );
  }

  withConfig<T>(requestFn: (config: AppConfig) => Observable<T>): Observable<T> {
    return this.waitForConfig().pipe(switchMap(c => requestFn(c)));
  }

  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('App config not initialized. Ensure config is loaded first.');
    }

    return this.config;
  }
}
